import { useCallback, useEffect, useRef, useState } from "react";
import { mediaUrl } from "../api.ts";

export interface Slide { key: string; kind: "photo" | "video" }

/**
 * Swipeable gallery: native scroll-snap (touch, trackpad and wheel behave as the platform intends),
 * plus arrows, keyboard, a counter and thumbnails. No autoplay on purpose: moving content under a
 * reader's thumb is an accessibility problem and fights with video playback.
 * RTL-safe: positions are derived from slide offsets, never from raw scrollLeft sign.
 */
export function Carousel({ slides, labels }: { slides: Slide[]; labels: { prev: string; next: string; of: string } }) {
  const track = useRef<HTMLDivElement>(null);
  const [i, setI] = useState(0);

  const sync = useCallback(() => {
    const el = track.current;
    if (!el || el.clientWidth === 0) return;
    const w = el.clientWidth;
    setI(Math.max(0, Math.min(slides.length - 1, Math.round(Math.abs(el.scrollLeft) / w))));
  }, [slides.length]);

  useEffect(() => { sync(); }, [sync]);

  const go = (n: number) => {
    const el = track.current;
    if (!el) return;
    const t = Math.max(0, Math.min(slides.length - 1, n));
    const child = el.children[t] as HTMLElement | undefined;
    child?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  };

  const rtl = typeof document !== "undefined" && document.documentElement.dir === "rtl";
  return (
    <div className="carousel" role="region" aria-roledescription="carousel" tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") { e.preventDefault(); go(i + (rtl ? -1 : 1)); }
        if (e.key === "ArrowLeft") { e.preventDefault(); go(i + (rtl ? 1 : -1)); }
      }}>
      <div className="car-track" ref={track} onScroll={sync}>
        {slides.map((s, n) => (
          <div className="car-slide" key={s.key} aria-label={`${n + 1} ${labels.of} ${slides.length}`}>
            {s.kind === "video"
              ? <video src={mediaUrl(s.key)} controls playsInline preload="metadata" />
              : <img src={mediaUrl(s.key)} alt="" loading={n === 0 ? "eager" : "lazy"} />}
          </div>
        ))}
      </div>
      {slides.length > 1 && (
        <>
          <button type="button" className="car-btn prev" onClick={() => go(i - 1)} disabled={i === 0} aria-label={labels.prev}>{rtl ? "›" : "‹"}</button>
          <button type="button" className="car-btn next" onClick={() => go(i + 1)} disabled={i === slides.length - 1} aria-label={labels.next}>{rtl ? "‹" : "›"}</button>
          <span className="car-count num" aria-live="polite">{i + 1} / {slides.length}</span>
          <div className="car-thumbs">
            {slides.map((s, n) => (
              <button type="button" key={s.key} className={n === i ? "on" : ""} onClick={() => go(n)} aria-label={`${n + 1} ${labels.of} ${slides.length}`} aria-current={n === i}>
                {s.kind === "video" ? <span className="vid">▶</span> : <img src={mediaUrl(s.key)} alt="" loading="lazy" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
